import * as React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { cn } from '../utils/cn';
import Glow from './Glow';
import LinkieLogo from './LinkieLogo';

type StatusScreenTone = 'brand' | 'danger';

type StatusScreenProps = {
  /** Rendered inside the tile — a 24px heroicon sizes itself from the tile. */
  icon: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Small pill above the title: a status code, the URL that was requested… */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  tone?: StatusScreenTone;
  showLogo?: boolean;
  /** Bring your own background — for screens that render outside any layout. */
  fullScreen?: boolean;
  className?: string;
};

// Opacity modifiers (`bg-primary/20`) silently compile to nothing on these
// colours — they are plain `var(--x)` values, and Tailwind 3 can only apply an
// alpha to a colour it can parse. Translucency has to come from `opacity-*` on
// an element of its own, which is what the glow below does.
const toneClasses: Record<StatusScreenTone, { glow: string; icon: string }> = {
  brand: { glow: 'bg-primary', icon: 'text-primary' },
  danger: { glow: 'bg-failed', icon: 'text-failed' },
};

const StatusScreen = ({
  icon,
  title,
  description,
  eyebrow,
  actions,
  tone = 'brand',
  showLogo = true,
  fullScreen = false,
  className,
}: StatusScreenProps) => {
  const prefersReducedMotion = useReducedMotion();
  const tones = toneClasses[tone];

  const container: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? { duration: 0.2 } : { staggerChildren: 0.08, delayChildren: 0.05 },
    },
  };

  const item: Variants = prefersReducedMotion
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 14 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
      };

  const content = (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className={cn('relative mx-auto flex w-full max-w-md flex-col items-center px-6 py-12 text-center', className)}
    >
      {showLogo ? (
        <motion.div variants={item} className="mb-10 text-lighter">
          <LinkieLogo className="w-24" />
        </motion.div>
      ) : null}

      <motion.div variants={item} className="relative mb-7 flex items-center justify-center">
        <div aria-hidden="true" className={cn('absolute h-28 w-28 rounded-full opacity-20 blur-3xl', tones.glow)} />
        <div
          className={cn(
            'relative flex h-20 w-20 items-center justify-center rounded-2xl bg-surface ring-1 ring-inset ring-secondary shadow-panel-shadow',
            tones.icon,
          )}
        >
          <span className="[&>svg]:h-9 [&>svg]:w-9">{icon}</span>
        </div>
      </motion.div>

      {eyebrow ? (
        <motion.span
          variants={item}
          className="mb-4 inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-dark px-3 py-1 text-xs font-medium text-light ring-1 ring-inset ring-secondary"
        >
          {eyebrow}
        </motion.span>
      ) : null}

      <motion.h1 variants={item} className="text-balance text-3xl font-bold leading-tight text-lighter sm:text-4xl">
        {title}
      </motion.h1>

      {description ? (
        <motion.p variants={item} className="mt-4 text-pretty text-base leading-relaxed text-light">
          {description}
        </motion.p>
      ) : null}

      {actions ? (
        <motion.div variants={item} className="mt-9 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row">
          {actions}
        </motion.div>
      ) : null}
    </motion.div>
  );

  if (!fullScreen) return content;

  return (
    <div className="min-h-viewport bg-main relative flex flex-col">
      <Glow />
      <div className="z-10 flex grow items-center justify-center">{content}</div>
    </div>
  );
};

export default StatusScreen;
